import { chunkStatus, describeChunk, measureChunk } from './measure.mjs';

const PAUSE_MS = 200;
const HOVER_MS = 700;
const TIMELINE_LIMIT = 10;
const MIN_PATH = 24;

function labelOf(node) {
  return (node.getAttribute('data-bloop-label') || node.textContent || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 40);
}

function kindOf(node) {
  return node.getAttribute('data-bloop-kind') || 'item';
}

function isField(node) {
  if (!node || !node.tagName) return false;
  const tag = node.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || node.isContentEditable;
}

export function createPerception({ onBeat, onActivity, getBloopPosition, onConfetti }) {
  let samples = [];
  let timeline = [];
  let lastEvent = null;
  let lastPointer = null;
  let lastGesture = null;
  let lastPath = null;
  let visitorAction = null;
  let typing = false;
  let hoverTimer = null;
  let hoverNode = null;
  let confettiHost = null;
  let selectionTimer = null;
  let scrollTimer = null;
  let scrollDistance = 0;
  let scrollStarted = 0;
  let lastScroll = window.scrollY;
  let distanceWord = 'not_on_screen';
  let approaches = [];
  let lastApproachAt = 0;
  let distHistory = [];
  let lastBloop = null;
  let bloopMovedTo = null;
  let bloopMovedAt = 0;
  let redirectedFlag = 'unknown';
  let reportedChase = false;
  let reportedRedirect = false;
  let relationTick = 0;
  let started = false;

  function pushTimeline(text) {
    timeline.push(text);
    if (timeline.length > TIMELINE_LIMIT) timeline.shift();
  }

  function finishChunk() {
    const chunk = samples;
    samples = [];
    if (chunk.length < 2) return;
    const measured = measureChunk(chunk);
    if (!measured || measured.path < MIN_PATH) return;
    lastGesture = describeChunk(measured);
    lastPath = chunk.map((point) => ({ x: point.x, y: point.y }));
    visitorAction = { type: 'move', element_id: null };
    pushTimeline(`visitor moved: ${lastGesture.speed} ${lastGesture.size} movement, ${lastGesture.turning}, ${lastGesture.reversals} reversals, ${lastGesture.direction}`);
    onBeat('gesture');
  }

  function onPointerSample(sample) {
    onActivity(sample);
    if (lastEvent && sample.t - lastEvent.t > PAUSE_MS && samples.length) finishChunk();
    lastEvent = sample;
    lastPointer = sample;
    const previous = samples[samples.length - 1];
    if (!previous || Math.hypot(sample.x - previous.x, sample.y - previous.y) >= 2) {
      samples.push(sample);
    }
    const status = chunkStatus(samples);
    if (status.close) finishChunk();
    if (sample.t - relationTick > 50) {
      relationTick = sample.t;
      updateRelation(sample);
    }
  }

  function updateRelation(sample) {
    const bloop = getBloopPosition();
    if (!bloop || !bloop.visible) {
      distanceWord = 'not_on_screen';
      lastBloop = null;
      return;
    }
    const distance = Math.hypot(sample.x - bloop.x, sample.y - bloop.y);
    if (distance < 40) distanceWord = 'overlapping';
    else if (distance < 140) distanceWord = 'near';
    else if (distance < 400) distanceWord = 'medium';
    else distanceWord = 'far';

    if (lastBloop && Math.hypot(bloop.x - lastBloop.x, bloop.y - lastBloop.y) > 80) {
      bloopMovedTo = { x: bloop.x, y: bloop.y };
      bloopMovedAt = sample.t;
      redirectedFlag = 'no';
      reportedRedirect = false;
    }
    lastBloop = { x: bloop.x, y: bloop.y };

    if (bloopMovedTo && sample.t - bloopMovedAt < 1500 && lastEvent) {
      const step = samples[samples.length - 1];
      const before = samples[samples.length - 2];
      if (step && before) {
        const vx = step.x - before.x;
        const vy = step.y - before.y;
        const towardX = bloopMovedTo.x - step.x;
        const towardY = bloopMovedTo.y - step.y;
        const speed = Math.hypot(vx, vy);
        const align = (vx * towardX + vy * towardY) / ((speed * Math.hypot(towardX, towardY)) || 1);
        if (speed > 4 && align > 0.75) {
          redirectedFlag = 'yes';
          if (!reportedRedirect) {
            reportedRedirect = true;
            pushTimeline('visitor redirected toward Bloop after Bloop moved');
            onBeat('chase');
          }
        }
      }
    }

    distHistory.push({ t: sample.t, distance });
    distHistory = distHistory.filter((entry) => sample.t - entry.t <= 8000);
    const earlier = distHistory.find((entry) => sample.t - entry.t >= 300);
    if (earlier && earlier.distance - distance > 60 && sample.t - lastApproachAt > 400) {
      lastApproachAt = sample.t;
      approaches.push(sample.t);
      approaches = approaches.filter((time) => sample.t - time < 8000);
      if (approaches.length >= 2 && !reportedChase) {
        reportedChase = true;
        pushTimeline('visitor moved toward Bloop again');
        onBeat('chase');
      }
    }
    if (!approaches.filter((time) => sample.t - time < 8000).length && redirectedFlag !== 'yes') {
      reportedChase = false;
    }
  }

  function relation() {
    const now = performance.now();
    const recent = approaches.filter((time) => now - time < 8000);
    let approachesRecently = 'none';
    if (recent.length === 1) approachesRecently = 'once';
    if (recent.length >= 2) approachesRecently = 'repeatedly';
    return {
      distance: distanceWord,
      approaches_recently: approachesRecently,
      redirected_after_bloop_moved: redirectedFlag,
    };
  }

  function visibleElements(pointer) {
    const nodes = document.querySelectorAll('[data-bloop-id]');
    const width = window.innerWidth;
    const height = window.innerHeight;
    const items = [];
    nodes.forEach((node) => {
      if (node.closest('[data-bloop-ignore]')) return;
      const rect = node.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > height || rect.right < 0 || rect.left > width) return;
      if (rect.width < 2 || rect.height < 2) return;
      const id = node.getAttribute('data-bloop-id');
      if (!id) return;
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const distance = pointer ? Math.hypot(centerX - pointer.x, centerY - pointer.y) : 0;
      items.push({
        id,
        description: `${kindOf(node)} '${labelOf(node)}'`,
        distance,
      });
    });
    items.sort((a, b) => a.distance - b.distance);
    const visible = {};
    for (const item of items) {
      if (visible[item.id] || Object.keys(visible).length >= 40) continue;
      visible[item.id] = item.description;
    }
    return visible;
  }

  function snapshot() {
    return {
      timeline: timeline.slice(),
      current_gesture: lastGesture,
      relation_to_bloop: relation(),
      visible_elements: visibleElements(lastPointer),
      visitor_action: visitorAction,
      last_prediction_failed: 'unknown',
    };
  }

  function onPointerMove(event) {
    if (event.pointerType === 'touch') return;
    onPointerSample({ x: event.clientX, y: event.clientY, t: performance.now() });
  }

  function onClick(event) {
    if (event.target.closest && event.target.closest('[data-bloop-ignore]')) return;
    const node = event.target.closest && event.target.closest('[data-bloop-id]');
    if (node) {
      const id = node.getAttribute('data-bloop-id');
      visitorAction = { type: 'click', element_id: id };
      pushTimeline(`visitor clicked the ${kindOf(node)} '${labelOf(node)}' (${id})`);
    } else if (event.target.closest && event.target.closest('a')) {
      visitorAction = { type: 'click', element_id: null };
      pushTimeline('visitor clicked a link');
    } else {
      visitorAction = { type: 'click', element_id: null };
      pushTimeline('visitor clicked the page');
    }
    onBeat('click');
  }

  function onPointerOver(event) {
    const host = event.target.closest && event.target.closest('[data-bloop-confetti]');
      if (host && host !== confettiHost) {
        confettiHost = host;
        if (onConfetti) onConfetti({ x: event.clientX, y: event.clientY });
      }
    const node = event.target.closest && event.target.closest('[data-bloop-id]');
    if (!node || node.closest('[data-bloop-ignore]') || node === hoverNode) return;
    hoverNode = node;
    window.clearTimeout(hoverTimer);
    hoverTimer = window.setTimeout(() => {
      if (hoverNode !== node) return;
      const id = node.getAttribute('data-bloop-id');
      visitorAction = { type: 'hover', element_id: id };
      pushTimeline(`visitor hovered the ${kindOf(node)} '${labelOf(node)}' (${id})`);
      onBeat('hover');
    }, HOVER_MS);
  }

  function onPointerOut(event) {
    const next = event.relatedTarget;
    if (confettiHost && (!next || !confettiHost.contains(next))) confettiHost = null;
    if (!hoverNode) return;
    if (next && hoverNode.contains(next)) return;
    hoverNode = null;
    window.clearTimeout(hoverTimer);
  }

  function onSelectionChange() {
    window.clearTimeout(selectionTimer);
    selectionTimer = window.setTimeout(() => {
      const selection = document.getSelection();
      if (!selection || selection.isCollapsed || !selection.toString().trim()) return;
      const parent = selection.anchorNode && selection.anchorNode.parentElement;
      const node = parent && parent.closest('[data-bloop-id]');
      if (node && !node.closest('[data-bloop-ignore]')) {
        const id = node.getAttribute('data-bloop-id');
        visitorAction = { type: 'select', element_id: id };
        pushTimeline(`visitor highlighted the ${kindOf(node)} '${labelOf(node)}' (${id})`);
      } else {
        visitorAction = { type: 'select', element_id: null };
        pushTimeline('visitor highlighted some text');
      }
      onBeat('select');
    }, 400);
  }

  function onCopy() {
    visitorAction = { type: 'copy', element_id: visitorAction ? visitorAction.element_id : null };
    pushTimeline('visitor copied a selection');
    onBeat('copy');
  }

  function onScroll() {
    const next = window.scrollY;
    const delta = next - lastScroll;
    lastScroll = next;
    if (!scrollDistance) scrollStarted = performance.now();
    scrollDistance += delta;
    window.clearTimeout(scrollTimer);
    scrollTimer = window.setTimeout(() => {
      const distance = scrollDistance;
      const elapsed = performance.now() - scrollStarted;
      scrollDistance = 0;
      if (Math.abs(distance) < 80) return;
      const direction = distance > 0 ? 'down' : 'up';
      const pace = elapsed > 0 && Math.abs(distance) / elapsed > 1.5 ? 'quickly' : 'steadily';
      visitorAction = { type: 'scroll', element_id: null };
      pushTimeline(`visitor scrolled ${direction} ${pace}`);
      onBeat('scroll');
    }, PAUSE_MS);
  }

  function onFocusIn(event) {
    if (isField(event.target)) typing = true;
  }

  function onFocusOut(event) {
    if (isField(event.target)) typing = false;
  }

  function start() {
    if (started) return;
    started = true;
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('click', onClick, true);
    window.addEventListener('pointerover', onPointerOver, true);
    window.addEventListener('pointerout', onPointerOut, true);
    document.addEventListener('selectionchange', onSelectionChange);
    window.addEventListener('copy', onCopy);
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
  }

  function stop() {
    if (!started) return;
    started = false;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('click', onClick, true);
    window.removeEventListener('pointerover', onPointerOver, true);
    window.removeEventListener('pointerout', onPointerOut, true);
    document.removeEventListener('selectionchange', onSelectionChange);
    window.removeEventListener('copy', onCopy);
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('focusin', onFocusIn);
    document.removeEventListener('focusout', onFocusOut);
    window.clearTimeout(hoverTimer);
    window.clearTimeout(selectionTimer);
    window.clearTimeout(scrollTimer);
  }

  function inject(points) {
    samples = [];
    lastEvent = null;
    for (const point of points) onPointerSample(point);
  }

  return {
    start,
    stop,
    snapshot,
    inject,
    note(text) { pushTimeline(text); },
    lastPath() { return lastPath ? lastPath.map((point) => ({ ...point })) : null; },
    isTyping() { return typing; },
  };
}
